// What the source document knew about an imported issue: source id, raw
// status, dates, answer, references, RFI header, contractor shorthand and
// where it came from (import file / sheet / row). Renders nothing for issues
// that were not imported.
import React, { useState } from "react";
import { Descriptions, Tag, Typography } from "antd";
import { FileSearchOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

const d = (v) => (v ? new Date(v).toLocaleDateString() : "");
const IMPACT = { HIGH: "red", MEDIUM: "gold", LOW: "green" };

export default function ImportedIssueDetails({ issue }) {
  const [open, setOpen] = useState(true);
  if (!issue) return null;
  const src = issue.source || {};
  const rfi = issue.rfi || {};
  const has = src.importId || issue.externalRef || issue.answer || rfi.number || (issue.references && issue.references.length) || issue.contractorLabel || issue.raisedAt || issue.topic || issue.assigneeName;
  if (!has) return null;
  const items = [];
  if (issue.externalRef) items.push({ key: "ref", label: "Source id", children: <b>{issue.externalRef}</b> });
  if (issue.category) items.push({ key: "cat", label: "Category", children: <Tag color={issue.category === "rfi" ? "purple" : issue.category === "action" ? "blue" : "default"}>{issue.category}</Tag> });
  if (issue.statusLabel) items.push({ key: "sl", label: "Status in source", children: issue.statusLabel });
  if (issue.raisedAt) items.push({ key: "ra", label: "Raised", children: d(issue.raisedAt) });
  if (issue.meetingDate) items.push({ key: "md", label: "Meeting", children: d(issue.meetingDate) });
  if (issue.topic) items.push({ key: "tp", label: "Topic", children: <Tag>{issue.topic}</Tag> });
  if (issue.assigneeName) items.push({ key: "an", label: "Assigned in source", children: <span>{issue.assigneeName}{issue.assigneeEmail ? <> · <a href={`mailto:${issue.assigneeEmail}`}>{issue.assigneeEmail}</a></> : null}</span> });
  if (issue.contractorLabel) items.push({ key: "cl", label: "Responsible (label)", children: <Tag color="gold">{issue.contractorLabel}</Tag> });
  if (issue.raisedByLabel) items.push({ key: "rb", label: "Raised by (label)", children: <Tag>{issue.raisedByLabel}</Tag> });
  if (rfi.number) items.push({ key: "rn", label: "RFI #", children: <b>{rfi.number}</b> });
  if (rfi.revision) items.push({ key: "rr", label: "Drawing revision", children: rfi.revision });
  if (rfi.levelArea) items.push({ key: "rl", label: "Level / area", children: rfi.levelArea });
  if (rfi.bimCostImpact) items.push({ key: "rc", label: "BIM cost impact", children: <Tag color={IMPACT[String(rfi.bimCostImpact).toUpperCase()]}>{rfi.bimCostImpact}</Tag> });
  if (rfi.bimScheduleImpact) items.push({ key: "rs", label: "BIM schedule impact", children: <Tag color={IMPACT[String(rfi.bimScheduleImpact).toUpperCase()]}>{rfi.bimScheduleImpact}</Tag> });
  if (rfi.fieldImpact) items.push({ key: "rf", label: "Field impact", children: <Tag color={IMPACT[String(rfi.fieldImpact).toUpperCase()]}>{rfi.fieldImpact}</Tag> });
  if (rfi.pages || rfi.issueCount) items.push({ key: "rp", label: "RFI size", children: `${rfi.pages || "?"} page(s), ${rfi.issueCount || "?"} issue(s)` });
  if (rfi.clientResponse) items.push({ key: "rcr", label: "Client response", children: rfi.clientResponse });
  if (issue.references && issue.references.length) items.push({ key: "refs", label: "References", children: issue.references.map((r) => <Tag key={r}>{r}</Tag>) });
  if (issue.answer) items.push({ key: "ans", label: "Answer / response", span: 2, children: <span style={{ whiteSpace: "pre-wrap" }}>{issue.answer}</span> });
  if (src.fileName) items.push({ key: "src", label: "Imported from", span: 2, children: (
    <span style={{ fontSize: 12 }}>
      {src.fileName}{src.table ? ` · ${src.table}` : ""}{src.row ? ` · row ${src.row}` : ""}
      {src.importedAt ? ` · ${new Date(src.importedAt).toLocaleDateString()}` : ""}
      {src.importId && issue.projectId ? <> · <Link to={`/schedule-import/${issue.projectId._id || issue.projectId}?importId=${src.importId}`}>view import</Link></> : null}
    </span>
  ) });
  return (
    <div style={{ margin: "6px 0 10px", border: "1px solid #eee", borderRadius: 6, background: "#fafafa" }}>
      <div style={{ padding: "4px 10px", fontSize: 11, letterSpacing: 0.4, textTransform: "uppercase", color: "#666", cursor: "pointer", display: "flex", justifyContent: "space-between" }} onClick={() => setOpen((v) => !v)}>
        <span><FileSearchOutlined /> Imported details</span>
        <span>{open ? "hide" : "show"}</span>
      </div>
      {open && (
        <div style={{ padding: "0 10px 8px" }}>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} items={items} />
        </div>
      )}
      <Typography.Text style={{ display: "none" }} />
    </div>
  );
}
